import { Check } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { ApiEstimate, EstimateStatus } from "@/lib/api/types"

// Consumer repair timeline, adapted from the old status page. Maps the
// estimate's EstimateStatus to a five-step plain-language journey. The shop
// drives the status transitions; the consumer just watches their car move
// through the shop.

interface Step {
  key: string
  label: string
  hint: string
}

const STEPS: Step[] = [
  { key: "submitted", label: "Photos submitted", hint: "We’ve got your photos" },
  { key: "reviewed", label: "Damage reviewed", hint: "The shop looked it over" },
  { key: "appointment", label: "Appointment set", hint: "Your time is booked" },
  { key: "in_repair", label: "In the shop", hint: "Repairs underway" },
  { key: "ready", label: "Ready for pickup", hint: "All done. Come grab it" },
]

// How far along the status puts us (index into STEPS that is the LAST completed
// step). Declined is handled separately by the caller.
function furthestStep(status: EstimateStatus, hasAppointment: boolean): number {
  switch (status) {
    case "Draft":
    case "Loading":
      return 0
    case "Pending":
      return hasAppointment ? 2 : 1
    case "Scheduled":
      return 2
    case "InProgress":
      return 3
    case "Completed":
      return 4
    default:
      return 0
  }
}

export function RepairTimeline({
  estimate,
  accent,
}: {
  estimate: ApiEstimate
  accent: string
}) {
  const hasAppointment = !!(
    estimate.metadata &&
    (estimate.metadata as Record<string, unknown>).appointment
  )
  const current = furthestStep(estimate.status, hasAppointment)
  const progress = Math.round((current / (STEPS.length - 1)) * 100)

  return (
    <div className="rounded-3xl border border-zinc-200/70 bg-white p-5 shadow-sm">
      {/* Progress bar */}
      <div className="mb-5">
        <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, backgroundColor: accent }}
          />
        </div>
      </div>

      <ol className="space-y-1">
        {STEPS.map((step, i) => {
          const done = i < current
          const active = i === current
          return (
            <li key={step.key} className="flex gap-3.5">
              <div className="flex flex-col items-center">
                <div
                  className={cx(
                    "grid size-8 shrink-0 place-items-center rounded-full transition-colors",
                    done || active ? "text-white" : "bg-zinc-100 text-zinc-400",
                  )}
                  style={done || active ? { backgroundColor: accent } : undefined}
                >
                  {done ? (
                    <Check className="size-4" strokeWidth={3} aria-hidden />
                  ) : (
                    <span className="text-[13px] font-bold">{i + 1}</span>
                  )}
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={cx("my-1 w-0.5 flex-1", done ? "" : "bg-zinc-100")}
                    style={done ? { backgroundColor: accent } : undefined}
                  />
                )}
              </div>
              <div className={cx("pb-5", !done && !active && "opacity-60")}>
                <p className="text-[14px] font-semibold leading-tight text-zinc-900">
                  {step.label}
                </p>
                <p className="mt-0.5 text-[13.5px] leading-snug text-zinc-600">
                  {active ? boldHint(step.hint) : step.hint}
                </p>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function boldHint(hint: string) {
  return <span className="font-semibold text-zinc-700">{hint}</span>
}
